import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  Avatar,
  Divider,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AccountBalanceWallet,
  AttachMoney,
  PointOfSale,
  TrendingUp,
  TrendingDown,
  Schedule,
  Done,
} from '@mui/icons-material';
import { ccyFormat } from '../helpers/helper';
import InfoBoxButton from './sales/InfoBoxButton';
import { loading } from '../config/config';
import { useCurrentUser } from './base/UserProvider';

const InfoBox = ({ icon, label, value, gradient }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 2,
        borderRadius: 3,
        background: gradient,
        color: 'white',
        boxShadow: 2,
        height: '100%',
      }}
    >
      <Avatar
        sx={{
          bgcolor: 'rgba(255,255,255,0.2)',
          width: 48,
          height: 48,
          mr: 2,
        }}
      >
        {icon}
      </Avatar>
      <Box>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          {label}
        </Typography>
        <Typography variant="h6" fontWeight="bold">
          {value}
        </Typography>
      </Box>
    </Box>
  </Grid>
);

const CashRegisterView = ({ data, onCashReal, onExpectedCash, onCloseCashRegister, onOpenNewCashRegister }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAdmin } = useCurrentUser();

  if (!data) return <Typography>No hay datos para mostrar</Typography>;

  const {
    status,
    open_at,
    close_at,
    detail_cash_register: detail,
  } = data;

  const detailList = [
    {
      icon: <AccountBalanceWallet />,
      label: 'Monto inicial',
      value: ccyFormat(detail.initial_mount),
      gradient: 'linear-gradient(45deg, #1976d2, #42a5f5)',
    },
    {
      icon: <PointOfSale />,
      label: 'Ventas en efectivo',
      value: ccyFormat(detail.cash_sale_total),
      gradient: 'linear-gradient(45deg, #2e7d32, #66bb6a)',
    },
    {
      icon: <AttachMoney />,
      label: 'Ventas por transferencia',
      value: ccyFormat(detail.trans_sale_total),
      gradient: 'linear-gradient(45deg, #388e3c, #81c784)',
    },
    {
      icon: <TrendingUp />,
      label: 'Señas',
      value: ccyFormat(detail.total_deposits),
      gradient: 'linear-gradient(45deg, #1565c0, #5c6bc0)',
    },
    {
      icon: <TrendingDown />,
      label: 'Devoluciones',
      value: ccyFormat(detail.total_refunds),
      gradient: 'linear-gradient(45deg, #ef6c00, #ffb74d)',
    },
    // solo admin
    ...(isAdmin ? [
      {
        icon: <TrendingUp />,
        label: 'Ingresos',
        value: ccyFormat(detail.total_income),
        gradient: 'linear-gradient(45deg, #00796b, #4db6ac)',
      },
      {
        icon: <TrendingDown />,
        label: 'Gastos',
        value: ccyFormat(detail.total_expenses),
        gradient: 'linear-gradient(45deg, #c62828, #ef5350)',
      },
      {
        icon: <AttachMoney />,
        label: 'Diferencia',
        value: ccyFormat(detail.difference),
        gradient:
          detail.difference === 0
            ? 'linear-gradient(45deg, #43a047, #66bb6a)'
            : 'linear-gradient(45deg, #d32f2f, #ef5350)',
      },
    ] : []),
  ];

  return (
    <Card sx={{ maxWidth: 1200, margin: 'auto', mt: isMobile ? 1 : 4, p: isMobile ? 1.5 : 3, borderRadius: 4, boxShadow: 6 }}>
      <Typography variant="h5" fontWeight="bold">
        Caja – {status === 'OPENED' ? 'Abierta' : 'Cerrada'}
      </Typography>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
          
          <Chip
            label={status === 'OPENED' ? 'Abierta' : 'Cerrada'}
            color={status === 'OPENED' ? 'success' : 'default'}
            icon={status === 'OPENED' ? <Done /> : <Schedule />}
            size={isMobile ? 'small' : 'medium'}
          />
          <Button
            variant="contained"
            color="error"
            size={isMobile ? 'small' : 'medium'}
            onClick={() => onCloseCashRegister && onCloseCashRegister()}
            disabled={loading || status === "CLOSED"}
            sx={isMobile ? { px: 1, minHeight: 30, fontSize: '0.75rem', textTransform: 'none' } : {}}
          >
            Cerrar Caja
          </Button>
          {status === "CLOSED" && isAdmin && (
            <Button
              variant="contained"
              color="warning"
              size={isMobile ? 'small' : 'medium'}
              onClick={() => onOpenNewCashRegister && onOpenNewCashRegister()}
              sx={isMobile ? { px: 1, minHeight: 30, fontSize: '0.75rem', textTransform: 'none' } : {}}
            >
              Abrir nueva caja
            </Button>
          )}
        </Box>

        <Grid container spacing={2} mb={3}>
          {/* <InfoBox
            icon={<Store />}
            label="Sucursal"
            value={store_id}
            gradient="linear-gradient(45deg, #6a1b9a, #ab47bc)" // violeta
          />
          <InfoBox
            icon={<AccountCircle />}
            label="Cajero"
            value={userId}
            gradient="linear-gradient(45deg, #283593, #5c6bc0)" // azul oscuro
          /> */}
          <InfoBox
            icon={<Schedule />}
            label="Apertura"
            value={new Date(open_at).toLocaleString()}
            gradient="linear-gradient(45deg, #00838f, #4dd0e1)" // celeste
          />
          {close_at && (
            <InfoBox
              icon={<Schedule />}
              label="Cierre"
              value={new Date(close_at).toLocaleString()}
              gradient="linear-gradient(45deg, #546e7a, #90a4ae)" // gris azulado
            />
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" mb={2}>
          Resumen financiero
        </Typography>

        <Grid container spacing={2}>
          {detailList.map((item, i) => (
            <InfoBox
              key={i}
              icon={item.icon}
              label={item.label}
              value={item.value}
              gradient={item.gradient}
            />
          ))}
          <InfoBoxButton
            icon={<AccountBalanceWallet />}
            label="Contado real"
            value={ccyFormat(detail.final_cash_amount)}
            gradient="linear-gradient(45deg, #ffb300, #ffca28)"
            onClick={() => onCashReal && onCashReal()}
            disabled={status === "CLOSED"}
          />
          {isAdmin && (
            <InfoBoxButton
              icon={<AccountBalanceWallet />}
              label="Esperado en caja"
              value={ccyFormat(detail.expected_cash_amount)}
              gradient="linear-gradient(45deg, #ffb300, #ffca28)"
              onClick={() => onExpectedCash && onExpectedCash()}
              disabled={false}
            />
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CashRegisterView;
